import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getUserToken } from "../../../helpers/local_storage_helpers";
import { Pre_Prod_BASE_URL, preProdToken } from "../../../constants/apis";
import axios from "axios";
import { getPaginationDisplay } from "../../../utils/pagination";

const Pagination = ({
  currentPage,
  pageSize,
  totalEntries,
  data,
  setData,
  onPageChange,
  onPageSizeChange = null,
  operation = "get",
  body = null,
}) => {
  const [selectedPageSize, setSelectedPageSize] = useState(pageSize);
  const [loadingPage, setLoadingPage] = useState(false);

  useEffect(() => {
    setSelectedPageSize(pageSize);
  }, [pageSize]);

  const { page: displayPage, totalPages } = getPaginationDisplay({
    currentPage,
    pageSize: selectedPageSize,
    totalEntries,
    data,
  });

  const handlePrevious = () => {
    if (displayPage <= 1 || loadingPage) return;

    if (setData && data?.previous) {
      fetchPage(data.previous.replace("http://", "https://"), displayPage - 1);
      return;
    }
    onPageChange(displayPage - 1);
  };

  const handleNext = () => {
    if (loadingPage) return;
    const canGoNext = data?.next || displayPage < totalPages;
    if (!canGoNext) return;

    if (setData && data?.next) {
      fetchPage(`${data.next.replace("http://", "https://")}`, displayPage + 1);
      return;
    }
    onPageChange(displayPage + 1);
  };

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setSelectedPageSize(newSize);
    onPageSizeChange && onPageSizeChange(newSize);
  };

  const fetchPage = async (link, nextPage) => {
    try {
      setLoadingPage(true);
      const response = operation === "get" ? await axios.get(link, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${link.includes(Pre_Prod_BASE_URL) ? preProdToken : getUserToken()}`,
        },
      }) : await axios.post(link, body, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${link.includes(Pre_Prod_BASE_URL) ? preProdToken : getUserToken()}`,
        },
      });

      if (response.status === 200) {
        setData(response.data);
        onPageChange(nextPage);
      } else {
        throw new Error(`Failed to fetch: ${response.status}`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || "Oops! Something went wrong with pagination.");
    } finally {
      setLoadingPage(false);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row justify-between items-center py-4 px-5 bg-white rounded-xl border border-gray-200 shadow-sm">
      {/* Page Size Selector */}
      {onPageSizeChange && (
        <div className="flex items-center gap-3 mb-3 sm:mb-0">
          <span className="text-sm font-medium text-gray-700">Show:</span>
          <select
            value={selectedPageSize}
            onChange={handlePageSizeChange}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-150"
            disabled={loadingPage}
          >
            {[10, 20, 30, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span className="text-sm text-gray-500">per page</span>
        </div>
      )}

      {/* Page Info */}
      <div className="text-sm text-gray-600 mb-3 sm:mb-0">
        {(totalEntries > 0) || (data && data?.results && data?.results?.length || data && data?.results?.records && data?.results?.records?.length) > 0 ? (
          <div className="flex items-center gap-3">
            <div className="px-3 py-1.5 bg-primary/10 text-primary rounded-lg font-medium text-sm">
              {totalEntries} total
            </div>
            <span className="text-gray-400">•</span>
            <span className="text-gray-700">
              <span className="font-semibold text-gray-800">{displayPage}</span>
              <span className="text-gray-500"> of </span>
              <span className="font-semibold text-gray-800">{totalPages}</span>
            </span>
          </div>
        ) : (
          <div className="text-gray-400 flex items-center gap-2">
            <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
            No data available
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center gap-2">
        {/* Previous Button */}
        <button
          onClick={handlePrevious}
          disabled={(displayPage <= 1 || loadingPage)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150"
          aria-label="Previous"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="hidden sm:inline text-sm font-medium">Previous</span>
        </button>

        {/* Current Page Indicator */}
        <div className="px-3 py-2 bg-primary text-white rounded-lg text-sm font-medium min-w-[2.5rem] text-center shadow-sm">
          {displayPage}
        </div>

        {/* Next Button */}
        <button
          onClick={handleNext}
          disabled={((!data?.next && displayPage >= totalPages) || loadingPage)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150"
          aria-label="Next"
        >
          <span className="hidden sm:inline text-sm font-medium">Next</span>
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Pagination;
